import {
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

import { type Session } from '../modal/session';

export interface Sessions {
  sessions: Array<Session>;
  pagination: Pagination;
}

export interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface GetSessionsAPIParams {
  name?: string;
  page?: number;
  limit?: number;
  isArchive?: boolean;
  isEventDefault?: boolean;
  isClassDefault?: boolean;
  /** When true, only sessions with endDate >= today (excludes past sessions) */
  excludePastSessions?: boolean;
  /** Studio: include archived sessions in results (admin enrollment in archived sessions) */
  includeArchivedSessions?: boolean;
}

export const getAllSessionsAPI = async (params?: GetSessionsAPIParams) => {
  const response = await baseAPI.get<Sessions>(`/session/search`, {
    params,
  });
  return response.data;
};

export const getAllSessionsOptions = (params?: GetSessionsAPIParams) => {
  return queryOptions({
    queryKey: [KEYS.SESSIONS, params],
    queryFn: () => getAllSessionsAPI(params),
    experimental_prefetchInRender: true,
    placeholderData: keepPreviousData,
  });
};

export const useGetAllSessions = (params?: GetSessionsAPIParams) => {
  return useQuery(getAllSessionsOptions(params));
};

const DEFAULT_INFINITE_PAGE_SIZE = 30;

type GetSessionsInfiniteParams = Omit<GetSessionsAPIParams, 'page'>;

export const useGetAllSessionsInfinite = (
  params?: GetSessionsInfiniteParams,
) => {
  const limit = params?.limit ?? DEFAULT_INFINITE_PAGE_SIZE;
  const paramsForKey: GetSessionsAPIParams = {
    ...params,
    limit,
    page: undefined,
  };

  return useInfiniteQuery({
    queryKey: [KEYS.SESSIONS, paramsForKey, 'infinite'] as const,
    queryFn: ({ pageParam }) =>
      getAllSessionsAPI({
        ...params,
        limit,
        page: pageParam,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, totalPages } = lastPage.pagination;
      if (totalPages === 0 || page >= totalPages) {
        return undefined;
      }
      return page + 1;
    },
    experimental_prefetchInRender: true,
    placeholderData: keepPreviousData,
  });
};
